// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Measure.js
//
// Holds the notes & rhythms for one measure of one voice of music.
import type { TimeSignatureAlias } from 'shared/definitions/musicTheory/rhythms';
import { rhythmicFractions } from 'shared/definitions/musicTheory/rhythms';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { clone } from 'shared/src/utils/clone';
import { isString } from 'shared/src/utils/isString';
import { findKey } from 'shared/src/utils/findKey';
import utils from '../utils';
import Rhythm from './rhythm';
import type { AriaStringOptions } from './note';
import Note from './note';
import NoteHolder from './noteHolder';
import Fraction from './fraction';
import TimeSignature from './timeSignature';
import Pointer from './pointer';
import Tuplet from './tuplet';
import Interval from './interval';
import type Clef from './clef';
import type Voice from './voice';
import type KeySignature from './keySignature';
import type CustomKeySignature from './customKeySignature';

export interface NotePointers {
  startPointer: Pointer;
  stopPointer: Pointer;
  durationPointer: Pointer;
  noteHolder: NoteHolder;
  beats?: number;
}

/**
 * A container for items that happen within a measure: noteHolders w/ rhythms,
 * barlines, etc...
 */
export default class Measure {
  noteHolders: NoteHolder[] = [];

  style: {
    fixedWidth: false | number;
    beginningBarline: undefined | string;
    endingBarline: undefined | string;
  } = {
    fixedWidth: false,
    beginningBarline: undefined,
    endingBarline: undefined,
  };

  /**
   * An object for holding noteholders & rhythms within a measure
   * @param noteHolders -- 0 or more noteHolders to add.
   */
  constructor(...noteHolders: NoteHolder[]) {
    // Function-global variables:

    // Splice in the noteHolders to this.noteHolders:
    this.splice(0, 0, ...noteHolders);

    return this;
  }

  setBeamDurations(durations: (Rhythm | string)[]): this {
    this.beamDurations = durations;
    return this;
  }

  private _beamDurations?: Rhythm[];

  set beamDurations(durations: (Rhythm | string)[] | undefined) {
    if (!durations) {
      this._beamDurations = undefined;
      return;
    }
    this._beamDurations = durations.map(Rhythm.fromRhythmOrString);
  }

  /**
   * Used in auto-beaming to determine beam proportions. When set,
   * Measure.beamDurations overrides TimeSignature.beamDurations. It should be
   * left undefined to use the default time signature beaming.
   */
  get beamDurations(): Rhythm[] | undefined {
    return this._beamDurations;
  }

  /**
   * Push a new NoteHolder into the measure:
   * @param noteOrNoteHolder - a note or noteHolder
   * @param rhythm - optional rhythm string 'e.' or Rhythm object
   */
  push(noteOrNoteHolder: Note | NoteHolder, rhythm?: Rhythm | string): this {
    const noteHolder = wrapNoteInHolderIfNeeded(noteOrNoteHolder);
    if (rhythm) {
      addRhythm(noteHolder, rhythm);
    }
    this.noteHolders.push(noteHolder);
    return this;
  }

  /**
   * Push a new NoteHolder from a noteString and rhythmString
   * @param noteOrString - to push
   * @param rhythmOrString - for the created NoteHolder
   */
  pushFromString(noteOrString: Note | NoteName | '', rhythmOrString: Rhythm | string) {
    let holder;
    if (noteOrString instanceof NoteHolder) {
      holder = noteOrString; // because, why not?
    } else {
      holder = new NoteHolder(noteOrString);
    }

    const rhythm = new Rhythm(rhythmOrString);
    this.push(holder, rhythm);
    return this;
  }

  /**
   * splice noteholders into or from the measure
   * @param indexOrNoteOrNoteHolder - to splice at
   * @param howManyToReplace - how many holders, if any, to replace
   * @param replacementNoteHolders - the NoteHolders to splice in
   */
  splice(
    indexOrNoteOrNoteHolder: number | Note | NoteHolder,
    howManyToReplace?: number,
    ...replacementNoteHolders: NoteHolder[]
  ): this {
    // follows exact same approach as JS splice function
    const index = this.indexOf(indexOrNoteOrNoteHolder);

    // Make the Splice:

    // if there are no notes to add, just delete them:
    if (replacementNoteHolders.length === 0) {
      this.noteHolders.splice(index, howManyToReplace);
      // otherwise, splice the other arguments in:
    } else {
      // The deleteCount argument (2nd arg of splice) is indeed optional -- as of TS 4.4,
      // however, the language definition is wrong.
      // @ts-ignore
      this.noteHolders.splice(index, howManyToReplace, ...replacementNoteHolders);
    }
    return this;
  }

  /**
   * Transpose all noteHolders in this measure by a specified interval.
   */
  transpose(interval: Interval | IntervalName) {
    if (!(interval instanceof Interval)) {
      interval = new Interval(interval);
    }

    this.noteHolders.forEach((nh) => nh.transpose(interval));
  }

  /**
   * Make a copy of a measure, with all child noteHolders cloned
   * as well.
   */
  clone(options: { doNotCloneNoteHolders?: boolean } = {}) {
    const newMeasure = new Measure();
    newMeasure.style = clone(this.style);
    newMeasure.noteHolders = options.doNotCloneNoteHolders
      ? this.noteHolders
      : this.noteHolders.map((nh) => nh.clone());
    return newMeasure;
  }

  /**
   * returns the index of a Note or NoteHolder within this measure
   */
  indexOf(noteOrNoteHolder: Note | NoteHolder | number): number {
    // returns noteHolders index for a particular note
    if (typeof noteOrNoteHolder === 'number') return noteOrNoteHolder;

    return this.noteHolders.findIndex((nh) => {
      if (noteOrNoteHolder instanceof Note) {
        return !!nh.notes.find((note) => note === noteOrNoteHolder);
      }
      return nh === noteOrNoteHolder;
    });
  }

  /**
   * @returns {NoteHolder[]} this.noteHolders
   */
  getNoteHolders() {
    return this.noteHolders;
  }

  /**
   * Get the last noteHolder in a measure.
   */
  getLastNoteHolder(): NoteHolder | false {
    if (this.noteHolders.length > 0) {
      return this.noteHolders[this.noteHolders.length - 1]!;
    }
    return false;
  }

  /**
   * Get the beat fraction of an index, Note, or NoteHolder if it exists. (Returns false otherwise.)
   */
  getBeat(indexOrNoteOrNoteHolder: Note | NoteHolder | number): Fraction | false {
    const index = this.indexOf(indexOrNoteOrNoteHolder);
    if (index === -1) return false;
    const beat = new Fraction(0, 1);
    // Add the rhythms of all the notes up to, but not including, this note
    // to get its beat fraction:
    this.noteHolders.slice(0, index).forEach((nh) => {
      if (nh.rhythm) {
        beat.add(nh.rhythm.getFraction());
      }
    });
    return beat;
  }

  /**
   * Get the total number of beats in a measure, with or without ghosts.
   * @param ignoreGhosts - default false, whether to count the rhythms of ghost
   * noteHolders within this measure
   */
  getTotalBeats(ignoreGhosts = false) {
    return this.noteHolders.reduce((sum, nh) => {
      if (ignoreGhosts && !nh.notes.length && !nh.rest) {
        return sum;
      }
      return sum.add(nh.rhythm?.getFraction() || 0);
    }, new Fraction(0, 1));
  }

  /**
   * Is this a pickup measure?
   */
  get isPickup() {
    return !!this.noteHolders.find((nh) => nh.isPickup);
  }

  /**
   * Is this measure beat complete?
   */
  isComplete(timeSigOrString: TimeSignature | TimeSignatureAlias, ignoreGhosts = false) {
    const timeSignature = TimeSignature.fromString(timeSigOrString);
    const incompleteMeasureDuration = this.getTotalBeats(ignoreGhosts); // fraction
    const timeSignatureDuration = timeSignature.measureFraction.clone();
    const whiteSpace = timeSignatureDuration.subtract(incompleteMeasureDuration);
    return whiteSpace.numerator === 0;
  }

  /**
   * Get a complete version of this measure. To modify the original measure,
   * use options.destination = this measure.
   * @param timeSigOrString - the time signature
   * @param options - measure completion options
   * to add the completion to.
   * @returns a Measure object that is beat complete.
   */
  getMeasureCompletion(
    timeSigOrString: TimeSignatureAlias | TimeSignature,
    options: {
      /** whether to add the NH as ghosts or rests. */
      ghost?: boolean;
      /** default = new Measure. The measure object */
      destination?: Measure;
    } = {}
  ) {
    const incompleteMeasureDuration = this.getTotalBeats(); // fraction
    let timeSignatureDuration: Fraction;
    if (timeSigOrString instanceof TimeSignature) {
      timeSignatureDuration = timeSigOrString.measureFraction.clone();
    } else {
      try {
        const timeSignature = TimeSignature.fromString(timeSigOrString);
        timeSignatureDuration = timeSignature.measureFraction.clone();
      } catch (e) {
        // This might happen when renderer is trying to fill out a measure, and
        // it detects that there are an odd number of beats in a measure. So
        // we can have something like '10/8' which will throw because it's not
        // a valid time signature.
        const fractionParts = timeSigOrString.split('/').map((num) => Number(num));
        timeSignatureDuration = new Fraction(...fractionParts);
      }
    }
    const ghost = !!options.ghost;
    const completionMeasure = new Measure();

    const whiteSpace = timeSignatureDuration.subtract(incompleteMeasureDuration);
    let subtractionFraction = new Fraction(1, 1);

    while (whiteSpace.numerator > 0) {
      while (subtractionFraction.greaterThan(whiteSpace)) {
        subtractionFraction = subtractionFraction.multiply(1, 2);
      }
      whiteSpace.subtract(subtractionFraction);
      const rhythmString = findKey(rhythmicFractions, subtractionFraction.toString());
      if (rhythmString !== undefined) {
        const nh = new NoteHolder('', rhythmString);
        completionMeasure.push(nh);
        completionMeasure.noteHolders[completionMeasure.noteHolders.length - 1]!.rest = !ghost;
      }
    }
    if (!this.isPickup) {
      completionMeasure.reverse();
    }

    // If there was no destination, just return the completion:
    if (!options.destination) return completionMeasure;

    // Otherwise add the completion to the destination and return it:
    if (this.isPickup) {
      options.destination.noteHolders = completionMeasure.noteHolders.concat(
        options.destination.noteHolders
      );
    } else {
      options.destination.noteHolders = options.destination.noteHolders.concat(
        completionMeasure.noteHolders
      );
    }

    return options.destination;
  }

  /**
   * Reverse the order of noteHolders in a measure in place.
   */
  reverse() {
    this.noteHolders = this.noteHolders.reverse();
    return this;
  }

  /**
   * get an array of Fractions representing the beats where the beginning
   * of a note occurs in this measure
   * @returns {Fraction[]} for each beat where a sound begins
   */
  getSoundedBeats(): Fraction[] {
    const beats: Fraction[] = [];
    const beat = new Fraction(0, 1);

    this.noteHolders.forEach((nh) => {
      if (!nh.isEmpty() && !nh.rhythm?.isTied) {
        beats.push(beat.clone());
      }
      if (nh.rhythm) {
        beat.add(nh.rhythm.getFraction());
      }
    });
    return beats;
  }

  /**
   * get an array of non-rest, non-ghosting, and non-tied noteHolders.
   * @returns an array of NoteHolders for each nh on which a sound begins
   */
  getSoundedNoteHolders(): NoteHolder[] {
    return this.noteHolders.filter((nh) => !nh.isEmpty() && !nh.rhythm?.isTied);
  }

  /**
   * Translate the measure into an array of objects with start, stop, duration
   * pointers and nh. Note this returns ALL pointers, including non-sounded
   * pointers.
   *
   * #### Caution: All noteHolders MUST have a rhythm defined or this will throw.
   * @param timeSig - required TimeSig type or throws
   * @param measureNumber - defaults zero.
   * @returns [{ startPointer, stopPointer, durationPointer, noteHolder }]
   */
  getPointers(timeSig: TimeSignature | TimeSignatureAlias, measureNumber = 0): NotePointers[] {
    if (typeof timeSig === 'undefined') {
      throw new Error('NaT: A time signature must be defined to get pointers for a voice.');
    }

    const measure = this.isComplete(timeSig, false)
      ? this
      : this.getMeasureCompletion(timeSig, {
          destination: this.clone({ doNotCloneNoteHolders: true }),
        });

    const P = Pointer;
    const currentPointer = new P(`${measureNumber}:0`, timeSig);

    const pointers: NotePointers[] = measure.noteHolders.map((holder) => {
      if (!holder.rhythm) {
        throw Error('All measure noteHolders must have a rhythm to get measure pointers.');
      }
      // set startBeat from currentPointer
      const startPointer = currentPointer.clone();
      // add the noteHolder's duration to the currentPointer
      const durationPointer = P.anythingToPointer(holder.rhythm, timeSig);
      // Update the current pointer by adding the duration:
      const stopPointer = currentPointer.add(durationPointer).simplify().clone();

      // Put them all in an object:
      return {
        startPointer,
        stopPointer,
        durationPointer,
        noteHolder: holder,
      };
    });

    // The pointers may have more notes than the original measure had,
    // if so we need to only return those for which there are noteHolders
    // in this measure:
    if (pointers.length !== this.noteHolders.length && this.isPickup) {
      if (this.isPickup) {
        return pointers.slice(-this.noteHolders.length);
      }
      return pointers.slice(0, this.noteHolders.length);
    }

    return pointers;
  }

  /**
   * Are two measures identical?
   */
  equals(comparisonMeasure: Measure) {
    // be certain they're the same length:
    if (this.noteHolders.length !== comparisonMeasure.noteHolders.length) return false;
    return this.noteHolders.every((thisNoteHolder, index) => {
      const thatNoteHolder = comparisonMeasure.noteHolders[index]!;
      if (!thisNoteHolder.equals(thatNoteHolder)) return false;
      if (thisNoteHolder.rhythm) {
        if (!thatNoteHolder.rhythm) return false;
        if (!thisNoteHolder.rhythm.equals(thatNoteHolder.rhythm)) return false;
        if (!!thisNoteHolder.rhythm.isTied !== !!thatNoteHolder.rhythm.isTied) return false;
      } else if (thatNoteHolder.rhythm) {
        return false;
      }
      return true;
    });
  }

  /**
   * Are two measures equal, with a rhythmic homophone? (Same start beats for nh?)
   */
  equalsRhythmicHomophone(comparisonMeasure: Measure) {
    // Compare rhythm first:
    const theseBeats = this.getSoundedBeats();
    const thoseBeats = comparisonMeasure.getSoundedBeats();
    if (theseBeats.length !== thoseBeats.length) return false;
    const beatsEqual = theseBeats.every((thisBeat, i) => {
      const thatBeat = thoseBeats[i]!;
      return thisBeat.equals(thatBeat);
    });
    if (!beatsEqual) return false;

    // Then compare pitches:
    const theseNoteHolders = this.getSoundedNoteHolders();
    const thoseNoteHolders = comparisonMeasure.getSoundedNoteHolders();
    if (theseNoteHolders.length !== thoseNoteHolders.length) return false;
    const noteHoldersEqual = theseNoteHolders.every((thisNoteHolder, i) => {
      const thatNoteHolder = thoseNoteHolders[i]!;
      return thisNoteHolder.equals(thatNoteHolder);
    });
    return noteHoldersEqual;
  }

  /**
   * Get the noteHolder after the holder or index.
   * @param holderOrIndex - of the current noteHolder
   * @param voice - optional, if included will return the first noteHolder of the
   * following measure if the holderOrIndex is the last noteHolder of this measure.
   */
  getHolderAfter(holderOrIndex: NoteHolder | number, voice?: Voice): NoteHolder | false {
    let index: number;
    if (holderOrIndex instanceof NoteHolder) {
      index = this.noteHolders.indexOf(holderOrIndex);
    } else {
      index = holderOrIndex;
    }
    if (index === -1) return false;
    if (this.noteHolders[index + 1]) return this.noteHolders[index + 1]!;
    if (voice) {
      const nextMeasureIndex = voice.measures.indexOf(this) + 1;
      const nextMeasure: undefined | Measure = voice.measures[nextMeasureIndex];
      if (nextMeasure) return nextMeasure.noteHolders[0] || false;
    }
    return false;
  }

  toAriaString(
    ariaOptions: AriaStringOptions = {},
    clef?: Clef,
    suppressEndingBarline = false,
    voice?: Voice,
    keySignature?: KeySignature | CustomKeySignature
  ) {
    const { barline = true } = ariaOptions;
    let text = '';

    // Add barline, and space if needed:
    if (barline) text += Measure.toAriaBarlineString(this.style.beginningBarline, '');
    text += text ? ' ' : '';

    let tupletStack: Tuplet[] = [];
    const nextHolderHasTuplet = (nextHolder: NoteHolder | false, tuplet: Tuplet): boolean => {
      if (!nextHolder) return false;
      return !!nextHolder.rhythm?.tuplets.includes(tuplet);
    };
    const holdersTexts = this.noteHolders.map((holder, index) => {
      let holderString = '';
      const nextHolder = this.getHolderAfter(index, voice);
      // if this has a holder not in the tuplet stack, open a bracket:
      holder.rhythm?.tuplets.forEach((tuplet) => {
        if (!tupletStack.includes(tuplet)) {
          tupletStack.push(tuplet);
          holderString += `begin ${tuplet}: (`;
        }
      });

      holderString += holder.toAriaString(ariaOptions, clef, keySignature);

      tupletStack = tupletStack.filter((tuplet: Tuplet) => {
        if (nextHolderHasTuplet(nextHolder, tuplet)) return true;
        holderString += `) end ${tuplet}`;
        return false;
      });
      if (nextHolder && nextHolder?.rhythm?.isTied) {
        const isLastHolder = this.noteHolders.length === index + 1;
        const endingBarlineText = Measure.toAriaBarlineString(this.style.endingBarline, 'barline');
        holderString += isLastHolder ? ` tied across the ${endingBarlineText} to ` : ' tied to ';
        if (isLastHolder) suppressEndingBarline = true;
      }
      return holderString;
    });
    text += utils.listToString(holdersTexts, { includeAnd: false, joiner: ';', oxfordComma: true });

    if (barline && !suppressEndingBarline) {
      text += text ? ' ' : '';
      text += Measure.toAriaBarlineString(this.style.endingBarline, 'barline');
    }
    return text;
  }

  static toAriaBarlineString(barline?: string, defaultString: string = ''): string {
    return barline
      ? Measure._barlineNames[barline as keyof typeof Measure._barlineNames]
      : defaultString;
  }

  /**
   * Convert a measure to a musicString representation
   * @param includeProperties like velocity, articulation, etc...
   * @returns {string} MusicFile representation of measure.
   */
  toString(includeProperties = false) {
    let str = '';
    if (
      typeof this.style.beginningBarline === 'string' &&
      this.style.beginningBarline in Measure._barlines
    ) {
      str += `${Measure._barlines[this.style.beginningBarline as keyof typeof Measure._barlines]} `;
    }

    let tupletStack: Tuplet[] = [];
    const nextHolderHasTuplet = (index: number, holders: NoteHolder[], tuplet: Tuplet) => {
      if (index === holders.length - 1) return false;
      return holders[index + 1]?.rhythm?.tuplets.includes(tuplet);
    };
    str += this.noteHolders
      .map((holder, index, holders) => {
        let holderString = '';
        // if this has a holder not in the tuplet stack, open a bracket:
        holder.rhythm?.tuplets.forEach(
          /** @param {Tuplet} tuplet */
          (tuplet) => {
            if (!tupletStack.includes(tuplet)) {
              tupletStack.push(tuplet);
              holderString += '[';
            }
          }
        );

        holderString += `${holder.toString(includeProperties)}`;

        tupletStack = tupletStack.filter(
          /** @param {Tuplet} tuplet */
          (tuplet) => {
            if (nextHolderHasTuplet(index, holders, tuplet)) return true;
            holderString += `]${tuplet.numNotes}:${tuplet.notesOccupied}`;
            return false;
          }
        );
        return holderString;
      })
      .join(' ');

    str += ' '; // so we have a rest before the barline

    if (
      typeof this.style.endingBarline === 'string' &&
      this.style.endingBarline in Measure._barlines
    ) {
      str += Measure._barlines[this.style.endingBarline as keyof typeof Measure._barlines];
    } else str += '|';
    return str;
  }

  /**
   * @param string - MusicFile representation of a Measure
   */
  fromString(string: string) {
    return Measure.fromString(string, this);
  }

  /**
   * @param string - MusicFile representation of a Measure
   */
  static fromString = (string: string, measure = new Measure()) => {
    // Look for barline types:
    //  ||| final
    //  :|| final repeat
    //  ||: initial repeat
    //  || double
    measure.noteHolders = [];
    delete measure.style.endingBarline;
    delete measure.style.beginningBarline;

    const barlines = {
      // Note: the pipe character has to be double escaped!!!
      ' |||': () => {
        measure.style.endingBarline = 'end';
      },
      ' :||': () => {
        measure.style.endingBarline = 'repeat_end';
      },
      '||: ': () => {
        measure.style.beginningBarline = 'repeat_begin';
      },
      ' ||': () => {
        if (!string.includes('|||')) {
          measure.style.endingBarline = 'double';
        }
      },
      ' |': () => {
        if (!string.includes('||')) {
          measure.style.endingBarline = 'single';
        }
      },
    };
    Object.keys(barlines).forEach((barline) => {
      const present = string.includes(barline);
      if (present) {
        barlines[barline as keyof typeof barlines]();
      }
    });

    [' |||', ' :||', '||: ', ' ||', ' |'].forEach((barline) => {
      string = string.replace(barline, '');
    });

    // Trim any external whitespace:
    string = string.trim();

    // Split on spaces & configure noteHolders.
    const holderStrings = string
      // Split on spaces, but NOT those within quotes: https://stackoverflow.com/questions/25663683/javascript-split-by-spaces-but-not-those-in-quotes
      .split(/ +(?=(?:(?:[^']*'){2})*[^']*$)/g)
      // Remove empty strings:
      .filter((s) => s !== '');

    const tupletStack: Tuplet[] = [];
    holderStrings.forEach((holderString) => {
      while (holderString.includes('[')) {
        tupletStack.push(new Tuplet('3:2')); // assume 3:2 for now...
        holderString = holderString.replace('[', '').trim();
      }

      const closingTupletRegEx = /(])(\d:\d)?/g;
      const noteHolder = NoteHolder.fromString(holderString.replace(closingTupletRegEx, ''));
      tupletStack.forEach(
        /** @param {Tuplet} tuplet */
        (tuplet) => tuplet.addNoteHolder(noteHolder)
      );

      while (holderString.includes(']')) {
        const re = /(])(\d:\d)?/;
        const ratio = re.exec(holderString)?.[2];
        const tuplet = tupletStack.pop();
        // tuplet should always be defined, if the string is well-formed, but
        // just in case...
        if (ratio && tuplet) tuplet.setRatio(ratio);
        holderString = holderString.replace(re, '').trim();
      }

      measure.noteHolders.push(noteHolder);
    });
    return measure;
  };

  private static _barlines = {
    single: '|',
    double: '||',
    final: '|||',
    /* eslint-disable @typescript-eslint/naming-convention */
    repeat_end: ':||',
    repeat_begin: '||:',
    repeat_both: ':||:',
    /* eslint-enable @typescript-eslint/naming-convention */
  };

  private static _barlineNames = {
    single: 'barline',
    double: 'double barline',
    final: 'final barline',
    repeat_end: 'repeat',
    repeat_begin: 'begin-repeat',
    repeat_both: 'repeat and begin-new-repeat',
  };
}

/**
 * Convert a Note to a NoteHolder
 * @param noteOrNoteHolder
 * @returns {NoteHolder}
 */
function wrapNoteInHolderIfNeeded(noteOrNoteHolder: Note | NoteHolder) {
  if (noteOrNoteHolder instanceof Note) {
    return new NoteHolder(noteOrNoteHolder);
  }
  if (noteOrNoteHolder instanceof NoteHolder) return noteOrNoteHolder;
  throw new Error(
    `noteOrNoteHolder must be a note or noteHolder, not a ${utils.typeof(
      noteOrNoteHolder
    )}: ${noteOrNoteHolder}`
  );
}

/**
 * Convert note or noteHolder without rhythm to a NoteHolder with rhythm.
 * @param noteOrNoteHolder the Note or NoteHolder to add
 * @param rhythm the Rhythm or rhythmString to attach to the NoteHolder
 * @returns {NoteHolder} with .rhythm property
 */
function addRhythm(noteOrNoteHolder: Note | NoteHolder, rhythm: Rhythm | string) {
  const noteHolder = wrapNoteInHolderIfNeeded(noteOrNoteHolder);
  if (rhythm instanceof Rhythm) {
    noteHolder.rhythm = rhythm;
    return noteHolder;
  }
  if (isString(rhythm)) {
    rhythm = new Rhythm(rhythm);
    noteHolder.rhythm = rhythm;
    return noteHolder;
  }
  if (noteOrNoteHolder instanceof NoteHolder && noteOrNoteHolder.rhythm instanceof Rhythm) {
    noteHolder.rhythm = noteOrNoteHolder.rhythm;
    return noteHolder;
  }
  throw new Error(
    `unable to add rhythm to noteHolder. noteHolder: ${noteHolder}, rhythm: ${rhythm}`
  );
}
